/**
 * TanStack Query bindings for the provider roster and its nested interfaces,
 * connections, import and export (ADR-040 §D-1 / §D-3).
 *
 * The provider level of the surface; :file:`use-provider-interfaces.ts` covers
 * the deployment-wide flat read and the trust-anchor upload. Split because the
 * two answer different questions and have different lifetimes: everything here
 * is **configuration** that is not fetched at all until an operator opens the
 * dialog (REQ-UI-156 — the roster "is not fetched until the surface opens; it is
 * configuration, not part of the roll-up").
 *
 * # Routes: flat reads, nested mutations
 *
 * Every interface and connection mutation nests under its provider because
 * REQ-PROVIDER-016 requires the audit record to name the full
 * ``provider / interface / connection`` path. An interface addressed through a
 * provider that does not own it is a 404, and a ``PUT`` may not re-home one
 * (409) — re-homing a live ingest listener under a different kill switch as a
 * side effect of an ordinary save is exactly what the three-level control exists
 * to make deliberate.
 *
 * # Cache invalidation
 *
 * A mutation at any level invalidates **both** the provider-scoped interface
 * list and the deployment-wide one. They feed different surfaces, and an
 * operator who deletes an interface here and closes the dialog must not find it
 * still listed elsewhere.
 *
 * Cross-references:
 *   - ADR-040 §D-1 (the entity), §D-3 (bundles, seed-then-own), §D-10 (RBAC).
 *   - REQ-PROVIDER-007 / -010 / -016 / -023 / -024.
 *   - REQ-UI-156 / -157 / -158 / -159.
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";
import { useSession } from "@/auth/session-context";
import {
  parseProviderInterfaceList,
  parseProviderList,
  type ProviderConfig,
  type ProviderInterfaceConfig,
  type ProviderInterfaceView,
  type ProviderView,
} from "@/providers/provider-model";
import type { InterfaceState } from "@/providers/provider-catalog";
import {
  PROVIDER_INTERFACES_KEY,
  PROVIDER_READER_ROLES,
} from "@/providers/use-provider-interfaces";

const BASE = "/api/admin/providers";

/** TanStack Query key for the provider roster. */
export const PROVIDERS_KEY: readonly unknown[] = ["provider-manager", "providers"];

/** Query key for one provider's interfaces — invalidated by every mutation beneath it. */
export function providerInterfacesKey(providerId: string): readonly unknown[] {
  return ["provider-manager", "providers", providerId, "interfaces"];
}

function providerPath(providerId: string): string {
  return `${BASE}/${encodeURIComponent(providerId)}`;
}

function interfacePath(providerId: string, interfaceId: string): string {
  return `${providerPath(providerId)}/interfaces/${encodeURIComponent(interfaceId)}`;
}

/**
 * Fetch the provider roster (``GET /api/admin/providers``).
 *
 * @param enabled Whether to issue the request at all. **Not a convenience** —
 *   REQ-UI-156 requires the roster not be fetched until the surface opens, and a
 *   caller that hoists this hook above the dialog would silently put a request
 *   on every session for a surface most sessions never open. Callers pass the
 *   dialog's open state; the role gate is applied on top of it here.
 */
export function useProviders(enabled = true): UseQueryResult<ProviderView[], ApiError> {
  const session = useSession();
  return useQuery<ProviderView[], ApiError>({
    queryKey: PROVIDERS_KEY,
    enabled: enabled && session.hasAnyRole(PROVIDER_READER_ROLES),
    queryFn: async () => parseProviderList(await apiFetch<unknown>(BASE)),
  });
}

/**
 * Fetch one provider's interfaces (``GET …/{provider_id}/interfaces``).
 *
 * This is the read that makes the three-level roster work for a
 * **non-privileged** session. Interface→provider parentage is privileged as a
 * *field* (REQ-UI-165), so a field-limited reader can never group the flat list
 * under its providers; asking for one provider's interfaces supplies the same
 * grouping from the path, and no field crosses the RBAC split to do it.
 *
 * @param providerId The selected provider, or ``null`` when nothing is selected.
 */
export function useProviderInterfacesFor(
  providerId: string | null,
): UseQueryResult<ProviderInterfaceView[], ApiError> {
  const session = useSession();
  return useQuery<ProviderInterfaceView[], ApiError>({
    queryKey: providerInterfacesKey(providerId ?? ""),
    enabled: providerId !== null && session.hasAnyRole(PROVIDER_READER_ROLES),
    queryFn: async () =>
      parseProviderInterfaceList(
        await apiFetch<unknown>(`${providerPath(providerId ?? "")}/interfaces`),
      ),
  });
}

/** Invalidate every provider-surface query — the roster, this provider's list, and the flat one. */
function useInvalidateProviderSurface(): (providerId: string) => void {
  const queryClient = useQueryClient();
  return (providerId: string) => {
    void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    void queryClient.invalidateQueries({ queryKey: providerInterfacesKey(providerId) });
    void queryClient.invalidateQueries({ queryKey: PROVIDER_INTERFACES_KEY });
  };
}

/** ``change_reference`` is omitted rather than sent empty, so the audit record carries no noise. */
function stateBody(
  state: InterfaceState,
  changeReference: string | undefined,
): Record<string, unknown> {
  const trimmed = changeReference?.trim();
  return trimmed ? { state, change_reference: trimmed } : { state };
}

/** Variables for {@link useSaveProvider}. */
export interface SaveProviderVars {
  config: ProviderConfig;
  /** Optional operator ticket / justification, carried onto the audit event. */
  changeReference?: string;
}

/**
 * Create or replace one provider (``PUT …/{provider_id}``).
 *
 * A whole-document replace on a caller-supplied UUID, so a double-submit is safe
 * on a control that provisions a collection source. The body's ``interfaces`` is
 * always empty — the server does not write a provider's interface set through
 * this route, so a rename cannot silently delete everything beneath it.
 */
export function useSaveProvider(): UseMutationResult<void, ApiError, SaveProviderVars> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, SaveProviderVars>({
    mutationFn: async ({ config, changeReference }) => {
      const body: components["schemas"]["PutProviderRequest"] = { provider: config };
      const trimmed = changeReference?.trim();
      if (trimmed) body.change_reference = trimmed;
      await apiFetch<unknown>(providerPath(config.provider_id), {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_result, { config }) => invalidate(config.provider_id),
  });
}

/**
 * Delete one provider (``DELETE …/{provider_id}``).
 *
 * **Cascades** to the provider's interfaces and their connections; the Provider
 * Manager's next reconcile tears down each listener. Materially larger than
 * deleting one interface, which is why the confirmation names what goes with it.
 */
export function useDeleteProvider(): UseMutationResult<void, ApiError, string> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, string>({
    mutationFn: async (providerId) => {
      await apiFetch<void>(providerPath(providerId), { method: "DELETE" });
    },
    onSuccess: (_result, providerId) => invalidate(providerId),
  });
}

/** Variables for a start/stop at any level. */
export interface SetProviderStateVars {
  providerId: string;
  state: InterfaceState;
  /** Optional operator ticket / justification, carried onto the audit event. */
  changeReference?: string;
}

/**
 * Start or stop a provider (``POST …/{provider_id}/state``) — the top-level kill
 * switch.
 *
 * Stopping a provider suppresses every interface beneath it **without**
 * overwriting their recorded states (REQ-PROVIDER-010), so starting it again
 * resumes exactly the set that was running before. The desired state is stated
 * absolutely; there is no toggle endpoint, so a double-click or a retry cannot
 * land the feed in the state the operator did not ask for.
 *
 * ⚠ On ingress this is a heavier act than its egress twin. A stopped egress path
 * backs up and is eventually noticed downstream; a stopped ingress path produces
 * **no backlog and no complaint, only silence** — and there is no queue behind it
 * that will drain when it starts. What the provider sent meanwhile is gone.
 */
export function useSetProviderState(): UseMutationResult<void, ApiError, SetProviderStateVars> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, SetProviderStateVars>({
    mutationFn: async ({ providerId, state, changeReference }) => {
      await apiFetch<unknown>(`${providerPath(providerId)}/state`, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/** Variables for {@link useSaveProviderInterface}. */
export interface SaveProviderInterfaceVars {
  providerId: string;
  config: ProviderInterfaceConfig;
}

/**
 * Create or replace one interface under its provider
 * (``PUT …/{provider_id}/interfaces/{interface_id}``).
 *
 * ``provider_id`` rides **both** the path and the body, and the BFF refuses a
 * disagreement with `provider_id_mismatch`. That is not redundancy for its own
 * sake: the envelope stays the generated ``PutProviderInterfaceRequest``, so
 * `tsc` still checks the body against the committed `openapi.yaml`, and
 * addressing an interface through the wrong provider surfaces as an explicit 400
 * rather than as a silently accepted move under a different kill switch.
 */
export function useSaveProviderInterface(): UseMutationResult<
  void,
  ApiError,
  SaveProviderInterfaceVars
> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, SaveProviderInterfaceVars>({
    mutationFn: async ({ providerId, config }) => {
      const body: components["schemas"]["PutProviderInterfaceRequest"] = {
        provider_id: providerId,
        interface: config,
      };
      await apiFetch<unknown>(interfacePath(providerId, config.interface_id), {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/** Variables addressing one interface under its provider. */
export interface ProviderInterfaceVars {
  providerId: string;
  interfaceId: string;
}

/**
 * Delete one interface (``DELETE …/{provider_id}/interfaces/{interface_id}``).
 *
 * Audited; not undoable. Its connections go with it.
 */
export function useDeleteProviderInterface(): UseMutationResult<
  void,
  ApiError,
  ProviderInterfaceVars
> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, ProviderInterfaceVars>({
    mutationFn: async ({ providerId, interfaceId }) => {
      await apiFetch<void>(interfacePath(providerId, interfaceId), { method: "DELETE" });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/** Variables for the interface start/stop mutation. */
export interface SetProviderInterfaceStateVars extends ProviderInterfaceVars {
  state: InterfaceState;
  changeReference?: string;
}

/** Start or stop one interface (``POST …/{provider_id}/interfaces/{id}/state``). */
export function useSetProviderInterfaceState(): UseMutationResult<
  void,
  ApiError,
  SetProviderInterfaceStateVars
> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, SetProviderInterfaceStateVars>({
    mutationFn: async ({ providerId, interfaceId, state, changeReference }) => {
      await apiFetch<unknown>(`${interfacePath(providerId, interfaceId)}/state`, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/** Variables for the per-connection start/stop mutation. */
export interface SetProviderConnectionStateVars extends SetProviderInterfaceStateVars {
  connectionId: string;
}

/** Start or stop one connection (``POST …/interfaces/{id}/connections/{id}/state``). */
export function useSetProviderConnectionState(): UseMutationResult<
  void,
  ApiError,
  SetProviderConnectionStateVars
> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, SetProviderConnectionStateVars>({
    mutationFn: async ({ providerId, interfaceId, connectionId, state, changeReference }) => {
      const path = `${interfacePath(providerId, interfaceId)}/connections/${encodeURIComponent(connectionId)}/state`;
      await apiFetch<unknown>(path, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/** Variables addressing one connection under its interface, under its provider. */
export interface ProviderConnectionVars extends ProviderInterfaceVars {
  connectionId: string;
}

/**
 * Delete one connection (``DELETE …/interfaces/{id}/connections/{id}``).
 *
 * **Distinct from stopping it** (REQ-UI-159). Stopping silences a connection and
 * keeps it, so starting the interface brings it back; this removes it from the
 * configuration and it does not. The two are separate endpoints precisely so
 * they land as separate audit events, and the UI words them apart for the same
 * reason — an operator reaching for a kill switch must not decommission a path
 * instead.
 *
 * Deleting the **last** connection is permitted: an interface with no
 * connections is a legitimate configured state (it is what `Add interface`
 * produces), so refusing would make the surface unable to return to a state it
 * can create. The interface then receives nothing, and says so.
 */
export function useDeleteProviderConnection(): UseMutationResult<
  void,
  ApiError,
  ProviderConnectionVars
> {
  const invalidate = useInvalidateProviderSurface();
  return useMutation<void, ApiError, ProviderConnectionVars>({
    mutationFn: async ({ providerId, interfaceId, connectionId }) => {
      const path = `${interfacePath(providerId, interfaceId)}/connections/${encodeURIComponent(connectionId)}`;
      await apiFetch<void>(path, { method: "DELETE" });
    },
    onSuccess: (_result, { providerId }) => invalidate(providerId),
  });
}

/**
 * The installer's verdict on a bundle, as ``POST …/providers/import`` reports it.
 *
 * The three outcomes are kept **apart**, not collapsed into a success flag.
 * Apply-once means an unchanged re-import changes nothing and an *edited*
 * re-import is refused rather than applied — and refusing is the point, because
 * applying would resurrect interfaces an operator deliberately deleted. A bare
 * 200 makes both look like a successful install (REQ-UI-157).
 */
export interface ProviderImportOutcome {
  applied: string[];
  already_applied: string[];
  /** ``(bundle, reason)`` pairs — the operator needs both to know whether to retry. */
  rejected: { bundle: string; reason: string }[];
  skipped: string[];
  /**
   * The subset of `applied` that overwrote a provider this deployment already had —
   * a deliberate re-import of an edited bundle. Kept apart so the dialog says
   * "replaced", not "installed", to an operator who just overwrote a live one.
   */
  replaced: string[];
  provider: { name: string } | null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Parse the import response.
 *
 * Hand-rolled for the reason the rest of this surface is
 * (:file:`provider-model`): the route returns :class:`JSONResponse`, so its
 * schema is ``{}`` in the committed ``openapi.yaml``. Missing keys degrade to
 * empty rather than throwing — unlike a *read* of live ingest configuration,
 * where a half-parsed body would hide a running feed. Here the write has already
 * happened server-side and the worst case is an under-reported summary, so
 * refusing to render it would tell the operator less, not more.
 */
export function parseProviderImportOutcome(body: unknown): ProviderImportOutcome {
  const record = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  const rejected = Array.isArray(record.rejected) ? record.rejected : [];
  const provider = record.provider;
  return {
    applied: asStringArray(record.applied),
    already_applied: asStringArray(record.already_applied),
    rejected: rejected.flatMap((entry) => {
      if (typeof entry !== "object" || entry === null) return [];
      const { bundle, reason } = entry as { bundle?: unknown; reason?: unknown };
      return typeof bundle === "string"
        ? [{ bundle, reason: typeof reason === "string" ? reason : "no reason given" }]
        : [];
    }),
    skipped: asStringArray(record.skipped),
    replaced: asStringArray(record.replaced),
    provider:
      typeof provider === "object" &&
      provider !== null &&
      typeof (provider as { name?: unknown }).name === "string"
        ? { name: (provider as { name: string }).name }
        : null,
  };
}

/**
 * Install a provider from a bundle on the operator's workstation
 * (``POST …/providers/import``) — REQ-UI-157 / REQ-PROVIDER-023.
 *
 * The BFF delegates to the same staged-bundle installer a boot-time install
 * uses, so an interactive import takes the identical validate → resolve →
 * anchor-check → store artifacts → persist → audit path. This hook therefore
 * does **not** audit, report success on its own, or interpret the outcome — it
 * returns the installer's verdict verbatim for the dialog to state.
 */
export function useImportProviderBundle(): UseMutationResult<
  ProviderImportOutcome,
  ApiError,
  File
> {
  const queryClient = useQueryClient();
  return useMutation<ProviderImportOutcome, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      // No Content-Type: the browser must set the multipart boundary.
      return parseProviderImportOutcome(
        await apiFetch<unknown>(`${BASE}/import`, { method: "POST", body: form }),
      );
    },
    // An import creates a provider this client has never seen, so there is no
    // provider id to scope the invalidation by — the roster and the flat list
    // are both stale and neither is addressed by `useInvalidateProviderSurface`.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
      void queryClient.invalidateQueries({ queryKey: PROVIDER_INTERFACES_KEY });
    },
  });
}

/** Variables for {@link useExportProviderBundle}. */
export interface ExportProviderVars {
  providerId: string;
  /** Used only for the download filename — the archive's contents come from the server. */
  providerName: string;
}

/**
 * Download one provider as an installable bundle (``GET …/{provider_id}/export``)
 * — REQ-UI-158 / REQ-PROVIDER-024.
 *
 * A **projection, not a replay**: every member is rendered from the control-plane
 * rows at the moment of the request, so a provider renamed in this dialog exports
 * under its new name.
 *
 * ⚠ **Both Meridian identity references stay behind** — the certificate as well
 * as the key, unlike the egress twin which ships its cert. The peer export ships
 * one because the upload path *proved* that material public; the provider server
 * certificate is minted at install beside its key and never passes a proving
 * path (REQ-PROVIDER-035). The bundle carries a note in their place so an
 * operator diffing an export against its provider can tell "this deployment has
 * no identity here" from "the export dropped it". The provider CA bundle *does*
 * travel: those anchors were validated when they were accepted.
 *
 * Read through ``rawResponse`` so this client's CSRF header, credential handling
 * and error mapping still apply to a route that answers with a file.
 */
export function useExportProviderBundle(): UseMutationResult<void, ApiError, ExportProviderVars> {
  return useMutation<void, ApiError, ExportProviderVars>({
    mutationFn: async ({ providerId, providerName }) => {
      const response = await apiFetch<Response>(`${providerPath(providerId)}/export`, {
        rawResponse: true,
      });
      downloadBlob(await response.blob(), `${providerName}-bundle.tar.gz`);
    },
  });
}

/**
 * Hand a fetched archive to the browser's own download handling.
 *
 * No-ops outside a browser so the call is safe in a test context that has not
 * stubbed the DOM — the mutation still resolves, which is what the caller
 * asserts on.
 */
function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}
